import { cva } from 'class-variance-authority';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import { vacanciesSlugs } from '@boss/constants/corporate-jobs';
import { IIVacancy, SimpleImage } from '@boss/types/corporate-jobs';
import { Hero, JobBlock, Pill } from '@boss/ui';
import { slugify } from '@boss/utils';

import { mapContentfulToNextLocale } from '../../utils/localeMapper';

type b2cProps = {
  locations: string[];
  jobs: (IIVacancy | null)[];
  locale: string;
  pageTitle: string;
  bannerImage?: SimpleImage | null;
};

type SingleJobBlockProps = {
  job: IIVacancy | null;
  locale: string;
  index: number;
};

const PillStyling = cva('cursor-pointer px-4 ', {
  variants: {
    active: {
      true: 'bg-faq text-white',
      false: 'text-faq border-faq relative border-1 border-solid bg-white',
    },
  },
});

const SingleJobBlock = ({ job, locale, index }: SingleJobBlockProps) => {
  const { t } = useTranslation(['jobs']);

  return (
    <JobBlock
      cta={{
        alternativeText: null,
        anchorClassName: '',
        href: `${vacanciesSlugs[mapContentfulToNextLocale(locale)]}/${slugify(job?.functieTitel ?? '')}/${
          job?.vacatureId
        }`,
        icon: undefined,
        iconPosition: 'left',
        label: t('applyButton'),
        type: 'primary',
      }}
      image={{
        imageAlt: t('paintDrop'),
        imageUrl: `/images/paintdrops/${index % 10}.png`,
        id: job?.vacatureId?.toString() ?? '',
      }}
      key={job?.vacatureId}
      location={job?.plaatsTewerkstelling?.gemeente}
      title={job?.functieTitel ?? ''}
      variant="b2c"
    />
  );
};

const B2c = ({ locations, jobs, locale, pageTitle, bannerImage }: b2cProps) => {
  const { t } = useTranslation(['jobs']);
  const [chosenLocation, setChosenLocation] = useState<string | undefined>(undefined);

  return (
    <>
      <Hero
        className="rounded-r-brand rounded-b-brand mb-12 overflow-hidden md:max-h-[316px] md:min-h-[316px]"
        image={bannerImage?.imageUrl ?? '/images/promotion_image_1.png'}
        imageAlt={bannerImage?.imageAlt ?? ''}
        imageWrapperClassName="pt-12 pr-12"
        title={pageTitle}
        variant="secondary"
      />
      {!!locations?.length && (
        <div className="mb-16 inline-flex w-full flex-row items-center gap-2 overflow-y-auto md:gap-4">
          <span>{t('filters')}</span>
          <button onClick={() => setChosenLocation(undefined)}>
            <Pill className={PillStyling({ active: chosenLocation === undefined })}>{t('filterLabel')}</Pill>
          </button>

          {locations.map(location => (
            <button key={location} onClick={() => setChosenLocation(location)}>
              <Pill className={PillStyling({ active: chosenLocation === location })}>{location}</Pill>
            </button>
          ))}
        </div>
      )}

      {chosenLocation ? (
        <div>
          <h2 className="h3 text-bold text-faq">{chosenLocation}</h2>
          <div className="mb-10">
            {jobs
              .filter(job => job?.handigeWeetjes?.locatie[0] === chosenLocation)
              .map(job => {
                const index = Math.floor(Math.random() * 10);

                if (job !== null) {
                  return <SingleJobBlock index={index} job={job} key={job?.vacatureId} locale={locale} />;
                }
              })}
          </div>
        </div>
      ) : (
        locations.map(location => {
          return (
            <div key={location}>
              <h2 className="h3 text-bold text-faq">{location}</h2>
              <div className="mb-10">
                {jobs
                  .filter(job => job?.handigeWeetjes?.locatie[0] === location)
                  .map(job => {
                    const index = Math.floor(Math.random() * 10);

                    if (job !== null) {
                      return <SingleJobBlock index={index} job={job} key={job?.vacatureId} locale={locale} />;
                    }
                  })}
              </div>
            </div>
          );
        })
      )}

      {jobs.length === 0 && <p className="body">{t('noResult')}</p>}
    </>
  );
};

export default B2c;
